/**
 * The Intensity VOCABULARY — the channel names, the level range, and the persisted shape.
 *
 * Authorised by: design-direction §3 ("a global Intensity dial — the single most important
 * control in the app") and its objection 1 (per-channel overrides beneath the dial);
 * implementation-brief §9 ("Intensity dial as design-token architecture");
 * docs/RULINGS.md R29.
 *
 * WHY IT LIVES IN `@carn/core` AND NOT IN `@carn/tokens` (RULINGS R29). The same vocabulary
 * was declared three times — once in `@carn/tokens` as `IntensityChannel`, once in
 * `@carn/core`'s `Settings.intensity` as `Record<string, number | 'auto'>`, and once again
 * in the schema — so an unknown channel name could be persisted and silently ignored. The
 * dependency graph settles where the single declaration goes: `@carn/schema` depends on
 * `@carn/core`, and `@carn/tokens` depends on `@carn/core`. Core is the base layer, so
 * nothing it needs may live above it. A string union of channel names is pure data — no
 * React, no I/O, no platform import — so nothing about it justified being said three times.
 *
 * ONE VOCABULARY, ONE PLACE, THREE CONSUMERS. `@carn/tokens` re-exports these names (the
 * same direction it already imports `Temperature` and `TEMPERATURES`), `@carn/schema`
 * persists this shape, and `Settings.intensity` is typed with it. **Adding a channel means
 * adding it to `INTENSITY_CHANNELS` below and nowhere else** — see design-direction §3
 * objection 1 and RULINGS R10 (the `contrast` channel).
 *
 * NO USER-VISIBLE STRINGS LIVE HERE. The channel keys are identifiers; their labels are
 * `intensity.channel.*` in `packages/copy/src/catalogue.ts`, in both registers.
 */

export const INTENSITY_LEVELS = [0, 1, 2, 3, 4] as const;
export type IntensityLevel = (typeof INTENSITY_LEVELS)[number];

/**
 * THE channel list. The single source a new channel is added to (RULINGS R29).
 *
 * Identifiers, not labels. Level 0 is the reference rendering and every higher level is a
 * delta from it (design-direction §3 objection 2), which `@carn/tokens`' `resolveTheme`
 * implements literally.
 */
export const INTENSITY_CHANNELS = [
  'colour',
  /**
   * Non-text contrast: boundaries, dividers, checkbox outlines, focus-ring weight and the
   * edge that separates one surface from another (design-direction §3, RULINGS R10, closing
   * DEVIATIONS D23).
   *
   * It sits next to `colour` because the two are what a user means by "how strong does this
   * look", and DELIBERATELY apart from it because they are not the same access need: a
   * low-stim user can hold `colour` at 0 and take `contrast` to 4, which is the combination
   * the dial alone could never express and the one product-concept §8.2's "high-contrast"
   * shopping mode needs. Unlike every other channel it has a FLOOR rather than a zero: WCAG
   * 1.4.11's 3:1 holds at level 0, because level 0 is the reference rendering
   * (design-direction §3 objection 2) and a reference rendering that fails a success
   * criterion is not a reference. It never touches text — §3 fixes text contrast at every
   * setting with no carve-out.
   */
  'contrast',
  'decor',
  'motion',
  'haptics',
  'celebration',
  'voice',
] as const;
export type IntensityChannel = (typeof INTENSITY_CHANNELS)[number];

/** `'auto'` follows the dial; a level pins the channel away from it (§3, objection 1). */
export type ChannelSetting = 'auto' | IntensityLevel;

/**
 * The mixer beneath the dial. TYPED, so an unknown channel name can no longer persist
 * silently (RULINGS R29) — it used to be `Record<string, number | 'auto'>`.
 */
export type IntensityOverrides = Readonly<Partial<Record<IntensityChannel, ChannelSetting>>>;

/** The persisted intensity shape. `Settings.intensity` is exactly this. */
export type IntensityPreferences = {
  /** The dial. One control, set in a 30-second first run (design-direction §3). */
  readonly dial: IntensityLevel;
  /** Absent or `'auto'` = follow the dial. */
  readonly overrides: IntensityOverrides;
};

/**
 * Steady is the shipped default: the dial's midpoint, not its reference end. Level 0 is the
 * reference for DESIGN (design-direction §3 objection 2), not the default for users.
 */
export const DEFAULT_INTENSITY_LEVEL: IntensityLevel = 2;

/**
 * THE SHIPPED WORDING DEFAULT: PLAIN WORDS. A new device reads functional labels until it asks
 * for the metaphors (docs/DEVIATIONS.md D30).
 *
 * It is a PIN ON THE `voice` CHANNEL, and deliberately not `Settings.literalLanguage = true`.
 * Both produce plain words — `CopyProvider` resolves the register as `theme.literal ||
 * theme.levels.voice === 0` — but only one of them leaves every control on the settings surface
 * telling the truth:
 *
 *   - `literalLanguage` is a boolean that SHORT-CIRCUITS the channel. Defaulting it to `true`
 *     would leave the mixer's own Wording row reading "Follow the dial" (level 2, playful) beside
 *     an app rendering plain words, and raising that row to 3 would change nothing — the exact
 *     control-that-lies defect `apps/mobile/src/screens/settings/wording.ts` was built to remove
 *     (critique 3.2).
 *   - A `voice` pin of 0 is what the wording control itself writes for "Plain words"
 *     (`wordingWrite`), so the default state is one the user could have produced by hand:
 *     `wordingChoice` reads back `plain`, the mixer's Wording row reads 0, and moving either
 *     control moves the register immediately.
 *
 * It is a PIN rather than a lower `DEFAULT_INTENSITY_LEVEL` because wording is one channel of six:
 * design-direction §3 keeps the dial's shipped default at Steady, and nothing about plain words
 * asks for less colour, motion or celebration.
 */
export const DEFAULT_VOICE_LEVEL: IntensityLevel = 0;

/**
 * The intensity a device ships with: the dial at its default, with wording pinned plain.
 *
 * Declared here, next to both constants it is made of, so that "the shipped default" is one
 * value rather than an object literal re-typed in `@carn/core`'s `DEFAULT_SETTINGS`, in
 * `@carn/ui`'s and in the app's. `@carn/tokens`' `DEFAULT_INTENSITY` stays deliberately
 * unpinned: it is the DIAL's default for a surface that has no wording control at all
 * (apps/web resolves a theme from it and drives the register with the boolean), and a pin
 * there would make that toggle inert.
 */
export const DEFAULT_INTENSITY_PREFERENCES: IntensityPreferences = {
  dial: DEFAULT_INTENSITY_LEVEL,
  overrides: { voice: DEFAULT_VOICE_LEVEL },
};

export function isIntensityChannel(value: string): value is IntensityChannel {
  return (INTENSITY_CHANNELS as readonly string[]).includes(value);
}

/**
 * Clamp any stored or synced value back into range. Sync from another device or a future
 * app version must never produce an unrenderable intensity.
 */
export function clampIntensityLevel(value: unknown, fallback: IntensityLevel): IntensityLevel {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const rounded = Math.round(value);
  if (rounded < 0) return 0;
  if (rounded > 4) return 4;
  return rounded as IntensityLevel;
}
